import { useCallback, useState } from "react";
import postsService from "../services/postsService";
import {
  showConfirm,
  showError,
  showToastError,
  showToastSuccess,
  showToastWarning,
} from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { buildVideoGenerationRequest } from "../utils/videoGenerationRequest";
import { postsCache } from "../utils/smartCache";
import type { UsePostsModalsReturn } from "./usePostsModals";

interface UsePostsMediaProps {
  loadPosts: (forceRefresh?: boolean) => Promise<void>;
  stats?: any;
  modals: UsePostsModalsReturn;
}

export interface UsePostsMediaReturn {
  // Estados de vídeo
  selectedVideoFile: File | null;
  setSelectedVideoFile: (file: File | null) => void;
  videoDestination: string;
  setVideoDestination: (dest: string) => void;
  videoCaption: string;
  setVideoCaption: (caption: string) => void;
  videoPrompt: string;
  setVideoPrompt: (prompt: string) => void;
  videoRestrictions: string;
  setVideoRestrictions: (restrictions: string) => void;
  videoDuration: number;
  setVideoDuration: (duration: number) => void;
  videoProvider: string;
  setVideoProvider: (provider: string) => void;
  logoFile: File | null;
  setLogoFile: (file: File | null) => void;
  logoPreview: string | null;
  setLogoPreview: (preview: string | null) => void;
  referenceImages: File[];
  setReferenceImages: (images: File[]) => void;
  referenceImagePreviews: string[];
  setReferenceImagePreviews: (previews: string[]) => void;
  videoTexts: Array<{
    id: string;
    text: string;
    startTime: number;
    endTime: number;
    position: string;
  }>;
  setVideoTexts: (
    texts: Array<{
      id: string;
      text: string;
      startTime: number;
      endTime: number;
      position: string;
    }>,
  ) => void;
  videoScript: string;
  setVideoScript: (script: string) => void;
  videoJobStatus: "idle" | "pending" | "processing" | "completed" | "failed";
  editingVideo: number | null;
  setEditingVideo: (id: number | null) => void;

  // Estados de imagem
  selectedImageFile: File | null;
  setSelectedImageFile: (file: File | null) => void;
  customPrompt: string;
  setCustomPrompt: (prompt: string) => void;
  imageRestrictions: string;
  setImageRestrictions: (restrictions: string) => void;
  imageProvider: string;
  setImageProvider: (provider: string) => void;
  imageLogoFile: File | null;
  setImageLogoFile: (file: File | null) => void;
  imageLogoPreview: string | null;
  setImageLogoPreview: (preview: string | null) => void;
  imageReferenceImages: File[];
  setImageReferenceImages: (images: File[]) => void;
  imageReferenceImagePreviews: string[];
  setImageReferenceImagePreviews: (previews: string[]) => void;
  imageJobStatus: "idle" | "pending" | "processing" | "completed" | "failed";

  // Handlers de vídeo
  handleUploadVideo: (postId: number) => Promise<void>;
  handleClearVideo: (postId: number) => Promise<void>;
  generateVideoWithAI: () => Promise<void>;
  handleEditVideo: (postId: number) => void;
  handleSaveVideoEdits: (
    postId: number,
    data: {
      texts: any[];
      script: string;
      pronunciationGuide?: Record<string, string>;
      editedTranscription?: string;
    },
  ) => Promise<void>;

  // Handlers de imagem
  handleUploadImage: () => Promise<void>;
  handleGenerateImage: () => Promise<void>;
  handleClearImage: (postId: number) => Promise<void>;
}

export function usePostsMedia({
  loadPosts,
  stats,
  modals,
}: UsePostsMediaProps): UsePostsMediaReturn {
  // Estados de vídeo
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [videoDestination, setVideoDestination] =
    useState<string>("instagram_feed");
  const [videoCaption, setVideoCaption] = useState<string>("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoRestrictions, setVideoRestrictions] = useState("");
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoProvider, setVideoProvider] = useState("veo");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [referenceImages, setReferenceImages] = useState<File[]>([]);
  const [referenceImagePreviews, setReferenceImagePreviews] = useState<
    string[]
  >([]);
  const [videoTexts, setVideoTexts] = useState<
    Array<{
      id: string;
      text: string;
      startTime: number;
      endTime: number;
      position: string;
    }>
  >([]);
  const [videoScript, setVideoScript] = useState("");
  const [videoJobStatus, setVideoJobStatus] = useState<
    "idle" | "pending" | "processing" | "completed" | "failed"
  >("idle");
  const [editingVideo, setEditingVideo] = useState<number | null>(null);

  // Estados de imagem
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [imageRestrictions, setImageRestrictions] = useState("");
  const [imageProvider, setImageProvider] = useState("dalle");
  const [imageLogoFile, setImageLogoFile] = useState<File | null>(null);
  const [imageLogoPreview, setImageLogoPreview] = useState<string | null>(null);
  const [imageReferenceImages, setImageReferenceImages] = useState<File[]>([]);
  const [imageReferenceImagePreviews, setImageReferenceImagePreviews] =
    useState<string[]>([]);
  const [imageJobStatus, setImageJobStatus] = useState<
    "idle" | "pending" | "processing" | "completed" | "failed"
  >("idle");

  // Handlers de vídeo
  const handleUploadVideo = useCallback(
    async (postId: number) => {
      if (!selectedVideoFile) return;
      try {
        modals.setUploadingVideo(postId);
        await postsService.uploadVideo(
          postId,
          selectedVideoFile,
          videoDestination,
          videoCaption,
        );
        showToastSuccess("Vídeo anexado ao post. Publique quando desejar.");
        modals.setShowVideoModal(null);
        setSelectedVideoFile(null);
        setVideoCaption("");
        await loadPosts(true);
      } catch (error: any) {
        console.error("Erro ao enviar vídeo:", error);
        const msg =
          error?.message ||
          error?.response?.data?.error ||
          "Erro ao enviar vídeo. Tente novamente.";
        showToastError(msg);
      } finally {
        modals.setUploadingVideo(null);
      }
    },
    [selectedVideoFile, videoDestination, videoCaption, modals, loadPosts],
  );

  const handleClearVideo = useCallback(
    async (postId: number) => {
      const confirmed = await showConfirm(
        "Tem certeza que deseja remover as informações de vídeo deste post?",
        "Remover vídeo",
        "Remover",
        "Cancelar",
      );
      if (!confirmed) {
        return;
      }

      try {
        await postsService.clearVideo(postId);
        showToastSuccess("Informações de vídeo removidas com sucesso");
        await loadPosts();
      } catch (error: any) {
        console.error("Erro ao limpar vídeo:", error);
        showToastError(
          `Erro ao limpar vídeo: ${error.message || "Erro desconhecido"}`,
        );
      }
    },
    [loadPosts],
  );

  const handleClearImage = useCallback(
    async (postId: number) => {
      const confirmed = await showConfirm(
        "Tem certeza que deseja remover as informações de imagem deste post?",
        "Remover imagem",
        "Remover",
        "Cancelar",
      );
      if (!confirmed) {
        return;
      }

      try {
        await postsService.clearImage(postId);
        showToastSuccess("Informações de imagem removidas com sucesso");
        await loadPosts();
      } catch (error: any) {
        console.error("Erro ao limpar imagem:", error);
        showToastError(
          `Erro ao limpar imagem: ${error.message || "Erro desconhecido"}`,
        );
      }
    },
    [loadPosts],
  );

  const generateVideoWithAI = useCallback(async () => {
    if (!stats?.agents_available) {
      showToastError(
        "Agentes não estão disponíveis. Configure as API keys necessárias.",
      );
      return;
    }

    if (!modals.showVideoGenerateModal) {
      return;
    }

    if (modals.generatingVideo === modals.showVideoGenerateModal) {
      return;
    }

    const postId = modals.showVideoGenerateModal;

    try {
      modals.setGeneratingVideo(postId);
      setVideoJobStatus("pending");

      const body = buildVideoGenerationRequest({
        videoProvider,
        videoPrompt,
        videoDuration,
        videoRestrictions,
        logoFile,
        referenceImages,
        videoTexts,
        videoScript,
      });

      const result = await apiService.post(
        API_ENDPOINTS.POSTS_EXTENDED.GENERATE_VIDEO.replace(
          "{id}",
          String(postId),
        ),
        body,
      );

      const jobId = result?.job_id || result?.task_id;
      let finalResult = result;
      if (jobId) {
        setVideoJobStatus("processing");
        finalResult = await postsService.pollMediaGenerationJob(
          postId,
          jobId,
          "video",
          {
            onStatus: (status) => {
              setVideoJobStatus(
                status === "processing"
                  ? "processing"
                  : status === "pending"
                    ? "pending"
                    : status === "completed"
                      ? "completed"
                      : status === "failed"
                        ? "failed"
                        : "pending",
              );
            },
          },
        );
      }

      if (finalResult?.success !== false) {
        showToastSuccess(
          "Vídeo gerado com sucesso! O arquivo foi salvo no post.",
        );
        await loadPosts(true);
        modals.setShowVideoGenerateModal(null);
        setVideoPrompt("");
        setVideoRestrictions("");
        setVideoDuration(8);
        setLogoFile(null);
        setLogoPreview(null);
        setReferenceImages([]);
        setReferenceImagePreviews([]);
        setVideoTexts([]);
        setVideoScript("");
      } else {
        if (
          finalResult?.error?.includes("quota") ||
          finalResult?.details?.reason === "quota_exceeded"
        ) {
          const solutions = finalResult?.details?.solutions || [];
          const alternatives =
            finalResult?.details?.temporary_alternatives || [];

          let quotaMessage = "🚫 Quota da API Veo Excedida\n\n";
          quotaMessage +=
            "A geração de vídeos está temporariamente indisponível.\n\n";
          quotaMessage +=
            "⏰ " +
            (finalResult?.details?.quota_reset ||
              "A quota é renovada automaticamente todo mês") +
            "\n\n";

          if (solutions.length > 0) {
            quotaMessage += "💡 Soluções:\n";
            solutions.forEach((solution: string, index: number) => {
              quotaMessage += `   ${index + 1}. ${solution}\n`;
            });
            quotaMessage += "\n";
          }

          if (alternatives.length > 0) {
            quotaMessage += "🎯 Alternativas temporárias:\n";
            alternatives.forEach((alt: string) => {
              quotaMessage += `   • ${alt}\n`;
            });
          }

          showError(quotaMessage, "Quota da API Veo Excedida");
          showToastError(
            "🚫 Quota da API Veo excedida - geração de vídeo indisponível",
          );
        } else {
          const errorMessage =
            finalResult?.message || finalResult?.error || "Erro ao gerar vídeo";
          showToastError(`Erro ao gerar vídeo: ${errorMessage}`);
        }
      }
    } catch (error: any) {
      if (
        error?.statusCode === 402 ||
        error?.code === "insufficient_credits" ||
        error?.errorData?.code === "insufficient_credits"
      ) {
        modals.setGeneratingVideo(null);
        return;
      }
      const errorData = (error as any)?.errorData;
      const quotaDetails = (error as any)?.quotaDetails || errorData?.details;
      const isQuotaError =
        (error as any)?.statusCode === 429 ||
        error?.message?.includes("quota") ||
        error?.message?.includes("Quota") ||
        errorData?.error?.includes("quota") ||
        errorData?.message?.includes("quota");

      if (!isQuotaError) {
        console.error("Erro ao gerar vídeo:", error);
      }

      if (isQuotaError) {
        const solutions = quotaDetails?.solutions || [
          "Aguarde a renovação automática da quota (mês que vem)",
          "Use imagens estáticas ao invés de vídeos",
          "Faça upgrade do plano Google AI para quota maior",
          "Entre em contato com suporte Google AI",
        ];
        const alternatives = quotaDetails?.temporary_alternatives || [
          "Gere posts sem vídeo por enquanto",
          "Use imagens geradas por IA (OpenAI, GLM)",
          "Crie conteúdo estático com texto e imagens",
        ];
        const quotaReset =
          quotaDetails?.quota_reset ||
          "A quota é renovada automaticamente todo mês";
        const provider = quotaDetails?.provider || "veo";

        let quotaMessage = `🚫 Quota da API ${provider.toUpperCase()} Excedida\n\n`;
        quotaMessage +=
          "A geração de vídeos está temporariamente indisponível.\n\n";
        quotaMessage += `⏰ ${quotaReset}\n\n`;

        if (solutions.length > 0) {
          quotaMessage += "💡 Soluções:\n";
          solutions.forEach((solution: string) => {
            quotaMessage += `   • ${solution}\n`;
          });
          quotaMessage += "\n";
        }

        if (alternatives.length > 0) {
          quotaMessage += "🎯 Alternativas temporárias:\n";
          alternatives.forEach((alt: string) => {
            quotaMessage += `   • ${alt}\n`;
          });
        }

        showError(
          quotaMessage,
          `Quota da API ${provider.toUpperCase()} Excedida`,
        );
        showToastError(
          `🚫 Quota da API ${provider.toUpperCase()} excedida - geração de vídeo indisponível`,
        );
        return;
      }

      let errorMessage = "Erro ao gerar vídeo.";
      if (error?.statusCode === 500) {
        errorMessage =
          "Erro no servidor ao gerar vídeo. Verifique se o post tem conteúdo e se a API key do Veo está configurada.";
      } else if (error?.statusCode === 400) {
        errorMessage =
          error?.message || "Prompt é obrigatório ou API key não configurada.";
      } else if (error?.statusCode === 504) {
        errorMessage =
          "Timeout na geração de vídeo. O vídeo pode estar sendo processado em background.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showError(`Erro ao gerar vídeo. ${errorMessage}`, "Erro ao gerar vídeo");
      showToastError(errorMessage);
    } finally {
      modals.setGeneratingVideo(null);
      setVideoJobStatus("idle");
    }
  }, [
    stats,
    modals,
    videoDuration,
    videoProvider,
    videoPrompt,
    videoRestrictions,
    logoFile,
    referenceImages,
    videoTexts,
    videoScript,
    loadPosts,
  ]);

  const handleEditVideo = useCallback(
    (postId: number) => {
      modals.setShowVideoEditorModal(postId);
    },
    [modals],
  );

  const handleSaveVideoEdits = useCallback(
    async (
      postId: number,
      data: {
        texts: any[];
        script: string;
        pronunciationGuide?: Record<string, string>;
        editedTranscription?: string;
      },
    ) => {
      setEditingVideo(postId);
      try {
        await apiService.post(`/posts/${postId}/edit-video`, data);
        showToastSuccess("Alterações do vídeo salvas com sucesso!");
        modals.setShowVideoEditorModal(null);
        await loadPosts();
      } catch (error: any) {
        console.error("Erro ao salvar edições do vídeo:", error);
        showToastError(
          `Erro ao salvar edições: ${error.message || "Erro desconhecido"}`,
        );
      } finally {
        setEditingVideo(null);
      }
    },
    [modals, loadPosts],
  );

  // Handlers de imagem
  const handleUploadImage = useCallback(async () => {
    if (!modals.showImageModal || !selectedImageFile) return;

    modals.setUploadingImage(modals.showImageModal);
    try {
      const response: any = await apiService.uploadFile(
        API_ENDPOINTS.POSTS_EXTENDED.UPLOAD_IMAGE.replace(
          "{id}",
          String(modals.showImageModal),
        ),
        selectedImageFile,
      );

      const imageInfo = response?.post?.image_info || response?.image_info;

      postsCache.invalidateAllPosts();
      showToastSuccess("Imagem enviada com sucesso!");
      modals.setShowImageModal(null);

      // Se houver variações, mostrar modal de variações
      if (imageInfo?.variations) {
        modals.setSelectedVariations(imageInfo.variations);
        modals.setSelectedOriginalUrl(imageInfo.public_url);
        modals.setShowSocialVariationsModal(true);
      }
      setSelectedImageFile(null);
      setCustomPrompt("");
      await loadPosts(true);
    } catch (error: any) {
      console.error("Erro ao enviar imagem:", error);
      const msg =
        error?.message ||
        error?.response?.data?.error ||
        "Erro ao enviar imagem. Tente novamente.";
      showToastError(msg);
    } finally {
      modals.setUploadingImage(null);
    }
  }, [modals, selectedImageFile, loadPosts]);

  const handleGenerateImage = useCallback(async () => {
    if (!modals.showImageModal) return;

    const imageSupportsAssets =
      imageProvider === "heygen" || imageProvider === "glm-image";
    const hasAssetFiles =
      imageSupportsAssets && (imageLogoFile || imageReferenceImages.length > 0);
    const postHasContent = modals.imageModalPostHasContent;
    const canGenerate =
      !!customPrompt.trim() || hasAssetFiles || postHasContent;
    if (!canGenerate) {
      showToastError(
        imageSupportsAssets
          ? "Digite um prompt, adicione logo/imagens de referência ou use um post que tenha conteúdo."
          : "Digite um prompt ou use um post que tenha conteúdo.",
      );
      return;
    }

    modals.setGeneratingImage(modals.showImageModal);
    setImageJobStatus("pending");
    try {
      const postId = modals.showImageModal;
      let enqueueResult;

      if (hasAssetFiles) {
        const formData = new FormData();
        if (customPrompt.trim()) {
          formData.append("prompt", customPrompt.trim());
        }
        formData.append("provider", imageProvider);

        if (imageRestrictions.trim()) {
          formData.append("restrictions", imageRestrictions.trim());
        }

        if (imageLogoFile) {
          formData.append("logo", imageLogoFile);
        }

        imageReferenceImages.forEach((img) => {
          formData.append("reference_images", img);
        });

        enqueueResult = await apiService.post(
          API_ENDPOINTS.POSTS_EXTENDED.GENERATE_IMAGE.replace(
            "{id}",
            String(postId),
          ),
          formData,
          { timeout: 60000 },
        );
      } else {
        const requestBody: Record<string, string> = {
          provider: imageProvider,
        };

        if (customPrompt.trim()) {
          requestBody.prompt = customPrompt.trim();
        }

        if (imageRestrictions.trim()) {
          requestBody.restrictions = imageRestrictions.trim();
        }

        enqueueResult = await apiService.post(
          API_ENDPOINTS.POSTS_EXTENDED.GENERATE_IMAGE.replace(
            "{id}",
            String(postId),
          ),
          requestBody,
          { timeout: 60000 },
        );
      }

      const jobId = enqueueResult?.job_id;
      if (!jobId) {
        throw new Error("Resposta inválida: job_id não retornado");
      }

      const result = await postsService.pollMediaGenerationJob(
        postId,
        jobId,
        "image",
        {
          onStatus: (status) => {
            setImageJobStatus(
              status === "processing"
                ? "processing"
                : status === "pending"
                  ? "pending"
                  : status === "completed"
                    ? "completed"
                    : status === "failed"
                      ? "failed"
                      : "pending",
            );
          },
        },
      );

      showToastSuccess("Imagem gerada com sucesso!");
      modals.setShowImageModal(null);

      const visionReview = (
        result as { vision_review?: { overall_pass?: boolean; issues?: string[] } }
      )?.vision_review;
      if (visionReview && visionReview.overall_pass === false) {
        const issues = visionReview.issues || [];
        showToastWarning(
          `IA detectou possíveis problemas no criativo: ${issues[0] || "revise antes de publicar"}`,
        );
      }

      const imageInfo =
        result?.image_info ||
        (result?.post as { image_info?: unknown } | undefined)?.image_info;

      if (
        imageInfo &&
        typeof imageInfo === "object" &&
        "variations" in imageInfo
      ) {
        const info = imageInfo as {
          variations?: Record<string, { public_url: string }>;
          public_url?: string;
        };
        if (info.variations) {
          modals.setSelectedVariations(info.variations);
          modals.setSelectedOriginalUrl(info.public_url ?? "");
          modals.setShowSocialVariationsModal(true);
        }
      }

      setCustomPrompt("");
      setImageRestrictions("");
      setImageLogoFile(null);
      setImageLogoPreview(null);
      setImageReferenceImages([]);
      setImageReferenceImagePreviews([]);
      await loadPosts();
    } catch (error: any) {
      console.error("Erro ao gerar imagem:", error);
      setImageJobStatus("failed");
      if (
        error?.statusCode === 402 ||
        error?.code === "insufficient_credits" ||
        error?.errorData?.code === "insufficient_credits"
      ) {
        return;
      }
      showToastError(error.message || "Erro ao gerar imagem. Tente novamente.");
    } finally {
      modals.setGeneratingImage(null);
      setImageJobStatus("idle");
    }
  }, [
    modals,
    customPrompt,
    imageProvider,
    imageRestrictions,
    imageLogoFile,
    imageReferenceImages,
    loadPosts,
  ]);

  return {
    // Estados de vídeo
    selectedVideoFile,
    setSelectedVideoFile,
    videoDestination,
    setVideoDestination,
    videoCaption,
    setVideoCaption,
    videoPrompt,
    setVideoPrompt,
    videoRestrictions,
    setVideoRestrictions,
    videoDuration,
    setVideoDuration,
    videoProvider,
    setVideoProvider,
    logoFile,
    setLogoFile,
    logoPreview,
    setLogoPreview,
    referenceImages,
    setReferenceImages,
    referenceImagePreviews,
    setReferenceImagePreviews,
    videoTexts,
    setVideoTexts,
    videoScript,
    setVideoScript,
    videoJobStatus,
    editingVideo,
    setEditingVideo,

    // Estados de imagem
    selectedImageFile,
    setSelectedImageFile,
    customPrompt,
    setCustomPrompt,
    imageRestrictions,
    setImageRestrictions,
    imageProvider,
    setImageProvider,
    imageLogoFile,
    setImageLogoFile,
    imageLogoPreview,
    setImageLogoPreview,
    imageReferenceImages,
    setImageReferenceImages,
    imageReferenceImagePreviews,
    setImageReferenceImagePreviews,
    imageJobStatus,

    // Handlers de vídeo
    handleUploadVideo,
    handleClearVideo,
    generateVideoWithAI,
    handleEditVideo,
    handleSaveVideoEdits,

    // Handlers de imagem
    handleUploadImage,
    handleGenerateImage,
    handleClearImage,
  };
}
